import React, { useEffect, useState, useCallback } from "react";
import "./ClaudeAgents.css";
import config from "../../config";
import AgentForm from "./AgentForm";
import AgentTestModal from "./AgentTestModal";
import ModelCatalogPanel from "./ModelCatalogPanel";

const baseUrl = config.api.baseUrl;
const adminBase = config.api.endpoints.claudeAgent.adminAgents;

async function listAgents() {
  const r = await fetch(baseUrl + adminBase, { credentials: "include" });
  return (await r.json()).data || [];
}
async function createAgent(payload) {
  const r = await fetch(baseUrl + adminBase, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const j = await r.json();
  if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
  return j.data;
}
async function patchAgent(id, payload) {
  const r = await fetch(`${baseUrl}${adminBase}/${id}`, {
    method: "PATCH", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const j = await r.json();
  if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
  return j.data;
}
async function deactivateAgent(id) {
  const r = await fetch(`${baseUrl}${adminBase}/${id}`, {
    method: "DELETE", credentials: "include",
  });
  const j = await r.json();
  if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export default function ClaudeAgentsTab() {
  const [agents, setAgents] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | {...agent}
  const [testingFor, setTestingFor] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState([]);

  const refresh = useCallback(async () => {
    try { setAgents(await listAgents()); }
    catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    fetch(baseUrl + "/claude-agent/admin/usage?days=30", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => setUsage(j.data || []))
      .catch(() => {});
  }, []);

  const onSave = async (payload) => {
    try {
      if (editing && editing._id) await patchAgent(editing._id, payload);
      else                         await createAgent(payload);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e.message);
      throw e;
    }
  };

  const onDeactivate = async (id) => {
    if (!window.confirm("Deactivate this agent? Existing chat threads will become read-only.")) return;
    await deactivateAgent(id);
    await refresh();
  };

  return (
    <div className="ca-admin">
      <div className="ca-admin-header">
        <h2>Claude Agents</h2>
        <button onClick={() => setEditing({})}>+ New Agent</button>
      </div>
      {error && <div className="ca-admin-error">{error}</div>}
      <ModelCatalogPanel />
      <div className="ca-admin-usage" style={{ marginBottom: 16 }}>
        <h3>Usage — last 30 days</h3>
        {usage.length === 0 ? (
          <p style={{ opacity: 0.6, fontSize: 13 }}>No usage recorded yet.</p>
        ) : (
          <table className="ca-admin-table">
            <thead><tr>
              <th>User</th><th>Agent</th><th>Messages</th><th>Tools</th>
              <th>Tokens (in/out)</th><th>Est. cost</th>
            </tr></thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.user_email + u.agent_id}>
                  <td>{u.user_email}</td>
                  <td>{u.agent_name}</td>
                  <td>{u.message_count}</td>
                  <td>{u.tool_call_count}</td>
                  <td>{u.input_tokens.toLocaleString()} / {u.output_tokens.toLocaleString()}</td>
                  <td>${u.cost_usd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <table className="ca-admin-table">
        <thead><tr>
          <th>Name</th><th>Model</th><th>Tools</th><th>MCP</th>
          <th>Active</th><th>Anthropic Agent</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a._id}>
              <td>
                {a.icon ? <span className="ca-agent-icon">{a.icon}</span> : "◆"}
                <strong style={{ marginLeft: 6 }}>{a.name}</strong>
                {a.description ? <div className="ca-admin-desc">{a.description}</div> : null}
              </td>
              <td>{a.model}</td>
              <td>{Object.entries(a.tools_config || {}).filter(([_, v]) => v).map(([k]) => k).join(", ") || "—"}</td>
              <td>{(a.mcp_servers || []).map((s) => s.name).join(", ") || "—"}</td>
              <td>{a.is_active ? "yes" : "no"}</td>
              <td className="ca-admin-mono">{a.anthropic_agent_id} v{a.anthropic_agent_version}</td>
              <td>
                <button onClick={() => setEditing(a)}>Edit</button>{" "}
                <button onClick={() => setTestingFor(a)}>Test</button>{" "}
                {a.is_active && <button onClick={() => onDeactivate(a._id)}>Deactivate</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing !== null && (
        <AgentForm value={editing} onCancel={() => setEditing(null)} onSave={onSave} />
      )}
      {testingFor && <AgentTestModal agent={testingFor} onClose={() => setTestingFor(null)} />}
    </div>
  );
}
